import React, {
  createContext,
  useContext,
  useEffect, useRef,
  useState,
} from "react";
import useDebounce from "hooks/useDebounce";
import { useChapterEditorDataContext } from "./ChapterEditorContext";
import { IDraftParagraph, IParagraph } from "models/wall";
import { createDraftParagraph, updateDraftParagraphApi } from "api/paragraphs";

export const emptyParagraphDraft = {
  date_from_day: null,
  date_from_month: null,
  date_from_year: null,
  date_to_day: null,
  date_to_month: null,
  date_to_year: null
}

type ParagraphDataContextType = {
  draftParagraph: IParagraph;
  updateDraftParagraph:  (v: Partial<IParagraph>) => void;
};

const ParagraphEditorDataContext = createContext<ParagraphDataContextType | undefined>(
  undefined
);

export const ParagraphEditorDataProvider = ({children, chapterId, initialDraft}: {
  children: React.ReactNode;
  chapterId: string;
  initialDraft: IDraftParagraph;
}) => {
  const { setIsSaving } = useChapterEditorDataContext();
  const { updateParagraphInChapter } = useChapterEditorDataContext();
  const [draftParagraph, setDraftParagraph] = useState<IDraftParagraph>(initialDraft);

  const updateDraftParagraph = (v: Partial<IDraftParagraph>) => {
    setDraftParagraph((p) => ({...p, ...v}));
  }

  const debouncedDraft = useDebounce(draftParagraph, 400);

  const prevDraftRef = useRef<IDraftParagraph>(debouncedDraft);

  const updateParagraph = async () => {
    if (!draftParagraph.uuid) return;

    const patch = Object.fromEntries(
      Object.entries(debouncedDraft).filter(([key, value]) => {
        const typedKey = key as keyof IDraftParagraph;
        return value !== prevDraftRef.current[typedKey];
      })
    ) as Partial<IDraftParagraph>;

    if (Object.keys(patch).length === 0) return;

    setIsSaving(true);

    try {
      await updateDraftParagraphApi(draftParagraph.uuid, patch);
      updateParagraphInChapter(draftParagraph.uuid, patch);
      prevDraftRef.current = debouncedDraft;
    } catch (e) {
      console.log(e);
    } finally {
      setIsSaving(false);
    }
  }

  useEffect(() => {
    updateParagraph();
  }, [debouncedDraft]);

  useEffect(() => {
    if (draftParagraph.uuid) return;
    setIsSaving(true);

    try {
      const createDraft = async () => {
        const response = await createDraftParagraph({
          ...draftParagraph,
          draft_chapter: chapterId,
        });
        setDraftParagraph(response);
      };

      createDraft();
    } catch (e) {
      console.log(e);
    } finally {
      setIsSaving(false);
    }
  }, [draftParagraph]);

  return (
    <ParagraphEditorDataContext.Provider value={{ draftParagraph, updateDraftParagraph }}>
      {children}
    </ParagraphEditorDataContext.Provider>
  );
};

export const useParagraphEditorDataContext = () => {
  const context = useContext(ParagraphEditorDataContext);
  if (!context) {
    throw new Error("useParagraphEditorContext must be used within a WallProvider");
  }
  return context;
};
